import { useState } from 'react';
import { SPORTS, MUSCLE_GROUPS, legacySportKey } from '../data/constants';
import BjjPromotionSummary from './BjjPromotionSummary';
import { calcStreak } from '../utils/streak';

const parseDateStr = dateStr => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const weekOfMonthLabel = date => {
    const first = new Date(date.getFullYear(), date.getMonth(), 1);
    const week = Math.ceil((date.getDate() + first.getDay()) / 7);
    return `${date.getMonth() + 1}월 ${week}주`;
};

function WeightChart({ weights }) {
    const data = [...weights].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-30);

    if (data.length === 0) {
        return (
            <div className="chart-wrap">
                <div className="chart-title">체중 추이</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>
                    체중 기록이 없어요.<br />기록 탭에서 체중을 입력해보세요.
                </div>
            </div>
        );
    }

    if (data.length === 1) {
        return (
            <div className="chart-wrap">
                <div className="chart-title">체중 추이</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>
                    {data[0].val}kg ({data[0].date})<br />2개 이상 기록하면 그래프가 표시돼요.
                </div>
            </div>
        );
    }

    const vals = data.map(w => +w.val);
    const minV = Math.min(...vals) - 0.5;
    const maxV = Math.max(...vals) + 0.5;
    const range = maxV - minV || 1;
    const W = 340, H = 100, padX = 28, padY = 18;

    const px = (i) => padX + (i / (data.length - 1)) * (W - padX * 2);
    const py = (v) => H - padY - ((v - minV) / range) * (H - padY * 2);

    const points = data.map((w, i) => `${px(i)},${py(+w.val)}`).join(' ');

    const latest = data[data.length - 1];
    const first = data[0];
    const diff = (+latest.val - +first.val).toFixed(1);
    const diffColor = +diff < 0 ? 'var(--accent)' : +diff > 0 ? 'var(--coral)' : 'var(--text3)';

    return (
        <div className="chart-wrap">
            <div className="chart-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>체중 추이 (최근 {data.length}회)</span>
                <span style={{ fontSize: 11, fontFamily: 'DM Mono,monospace', color: diffColor }}>
                    {+diff > 0 ? '+' : ''}{diff}kg
                </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ height: 110, width: '100%', overflow: 'visible' }}>
                {/* Y축 참고선 */}
                {[0, 0.5, 1].map(t => {
                    const yVal = minV + t * range;
                    const y = py(yVal);
                    return (
                        <g key={t}>
                            <line x1={padX} y1={y} x2={W - padX} y2={y} stroke="var(--bg3)" strokeWidth="1" />
                            <text x={padX - 4} y={y + 4} fontSize="8" fill="var(--text3)" textAnchor="end" fontFamily="DM Mono,monospace">
                                {yVal.toFixed(1)}
                            </text>
                        </g>
                    );
                })}
                {/* 그라데이션 영역 */}
                <defs>
                    <linearGradient id="wGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#c8f764" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#c8f764" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <polygon
                    points={`${px(0)},${H - padY} ${points} ${px(data.length - 1)},${H - padY}`}
                    fill="url(#wGrad)"
                />
                <polyline points={points} fill="none" stroke="#c8f764" strokeWidth="1.8" strokeLinejoin="round" />
                {data.map((w, i) => (
                    <circle key={i} cx={px(i)} cy={py(+w.val)} r="2.5" fill="#c8f764" />
                ))}
                {/* 첫/마지막 라벨 */}
                {[0, data.length - 1].map(i => (
                    <text key={i} x={px(i)} y={py(+data[i].val) - 8} fontSize="10" fill="var(--text2)"
                        textAnchor={i === 0 ? 'start' : 'end'} fontFamily="DM Mono,monospace">
                        {data[i].val}kg
                    </text>
                ))}
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 2, fontFamily: 'DM Mono,monospace' }}>
                <span>{first.date}</span>
                <span>{latest.date}</span>
            </div>
        </div>
    );
}

const toDateKey = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const estimateOneRm = (weight, reps) => {
    const w = +weight || 0;
    const r = +reps || 1;
    if (!w) return 0;
    if (r <= 1) return w;
    return Math.round(w * (1 + Math.min(r, 30) / 30));
};

function WeightPrTrend({ records }) {
    const [unit, setUnit] = useState('day');
    const [muscle, setMuscle] = useState('all');
    const W = 340, H = 118, padX = 30, padY = 22;

    const bucketLabel = (dateStr) => {
        const d = parseDateStr(dateStr);
        if (unit === 'month') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (unit === 'week') {
            const start = new Date(d);
            start.setDate(d.getDate() - d.getDay());
            return toDateKey(start);
        }
        return dateStr;
    };
    const displayLabel = (key) => {
        const d = parseDateStr(key);
        if (unit === 'month') return `${d.getMonth() + 1}월`;
        if (unit === 'week') return weekOfMonthLabel(d);
        return `${d.getMonth() + 1}/${d.getDate()}`;
    };

    const bucketMap = {};
    records
        .filter(r => r.sport === 'weight')
        .forEach(r => {
            const exList = r.exercises?.length
                ? r.exercises
                : (r.exerciseName ? [{ name: r.exerciseName, muscleGroup: r.muscleGroup || '', sets: r.sets || [] }] : []);
            exList.forEach(ex => {
                if (muscle !== 'all' && ex.muscleGroup !== muscle) return;
                (ex.sets || []).forEach(set => {
                    const oneRm = estimateOneRm(set.weight, set.reps);
                    if (!oneRm) return;
                    const key = bucketLabel(r.date);
                    bucketMap[key] = Math.max(bucketMap[key] || 0, oneRm);
                });
            });
        });

    let best = 0;
    const data = Object.entries(bucketMap)
        .sort(([a], [b]) => parseDateStr(a) - parseDateStr(b))
        .map(([key, val]) => {
            best = Math.max(best, val);
            return { key, val: best };
        })
        .slice(-14);

    const first = data[0];
    const latest = data[data.length - 1];
    const diff = first && latest ? latest.val - first.val : 0;

    const renderChart = () => {
        if (data.length === 0) {
            return (
                <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '20px 0 8px' }}>
                    해당 조건의 웨이트 PR 기록이 없어요.
                </div>
            );
        }
        if (data.length === 1) {
            return (
                <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '20px 0 8px' }}>
                    현재 최고 추정 1RM은 {latest.val}kg예요.<br />2개 이상 기록하면 추이가 표시돼요.
                </div>
            );
        }

        const vals = data.map(d => d.val);
        const minV = Math.max(0, Math.min(...vals) - 5);
        const maxV = Math.max(...vals) + 5;
        const range = maxV - minV || 1;
        const px = (i) => padX + (i / (data.length - 1)) * (W - padX * 2);
        const py = (v) => H - padY - ((v - minV) / range) * (H - padY * 2);
        const points = data.map((d, i) => `${px(i)},${py(d.val)}`).join(' ');

        return (
            <>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 126, overflow: 'visible' }}>
                    {[0, 0.5, 1].map(t => {
                        const yVal = minV + t * range;
                        const y = py(yVal);
                        return (
                            <g key={t}>
                                <line x1={padX} y1={y} x2={W - padX} y2={y} stroke="var(--bg3)" strokeWidth="1" />
                                <text x={padX - 5} y={y + 4} fontSize="8" fill="var(--text3)" textAnchor="end" fontFamily="DM Mono,monospace">
                                    {Math.round(yVal)}
                                </text>
                            </g>
                        );
                    })}
                    <polyline points={points} fill="none" stroke="#5ba4f5" strokeWidth="2" strokeLinejoin="round" />
                    {data.map((d, i) => <circle key={d.key} cx={px(i)} cy={py(d.val)} r="2.6" fill="#5ba4f5" />)}
                    <text x={px(data.length - 1)} y={py(latest.val) - 8} fontSize="10" fill="var(--text2)" textAnchor="end" fontFamily="DM Mono,monospace">
                        {latest.val}kg
                    </text>
                </svg>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono,monospace' }}>
                    <span>{displayLabel(first.key)}</span>
                    <span>{displayLabel(latest.key)}</span>
                </div>
            </>
        );
    };

    return (
        <div className="chart-wrap">
            <div className="chart-title">
                <span>웨이트 PR 추이</span>
                <span style={{ fontFamily: 'DM Mono,monospace', color: diff > 0 ? 'var(--accent)' : 'var(--text3)' }}>
                    {latest ? `${latest.val}kg${diff > 0 ? ` +${diff}` : ''}` : '-'}
                </span>
            </div>
            <div className="pr-filter-row">
                <div className="toggle-row pr-unit-row">
                    {[
                        ['day', '일'],
                        ['week', '주'],
                        ['month', '월'],
                    ].map(([key, label]) => (
                        <button key={key} className={`toggle-btn${unit === key ? ' active' : ''}`} onClick={() => setUnit(key)}>
                            {label}
                        </button>
                    ))}
                </div>
                <select className="form-input pr-muscle-select" value={muscle} onChange={e => setMuscle(e.target.value)}>
                    <option value="all">전체</option>
                    {MUSCLE_GROUPS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
            </div>
            {renderChart()}
        </div>
    );
}

export default function Stats({ records, weights, goals, setGoals, promotions = [], showGoals = true }) {
    const [showAddGoal, setShowAddGoal] = useState(false);
    const [newGoal, setNewGoal] = useState({ sport: 'all', type: 'weekly', target: 3 });

    const now2 = new Date();
    const goalProgress = (g) => {
        const relevant = records.filter(r => {
            const d = parseDateStr(r.date);
            const matchSport = g.sport === 'all' || r.sport === g.sport;
            if (g.type === 'weekly') {
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const dow = today.getDay();
                const weekStart = new Date(today); weekStart.setDate(today.getDate() - dow);
                const rd = parseDateStr(r.date); rd.setHours(0, 0, 0, 0);
                return matchSport && rd >= weekStart && rd <= today;
            } else {
                return matchSport && d.getFullYear() === now2.getFullYear() && d.getMonth() === now2.getMonth();
            }
        });
        return relevant.length;
    };

    const addGoal = () => {
        if (!newGoal.target || +newGoal.target < 1) return;
        setGoals([...(goals || []), { ...newGoal, target: +newGoal.target, id: Date.now() }]);
        setShowAddGoal(false);
        setNewGoal({ sport: 'all', type: 'weekly', target: 3 });
    };

    const removeGoal = (id) => setGoals((goals || []).filter(g => g.id !== id));
    // 종목별 카운트
    const visibleSports = Object.entries(SPORTS).filter(([, v]) => !v.legacy);
    const sportCounts = Object.fromEntries(visibleSports.map(([k]) => [k, 0]));
    records.forEach(r => {
        const key = legacySportKey(r);
        if (sportCounts[key] !== undefined) sportCounts[key]++;
    });
    const recordedSports = visibleSports.filter(([k]) => sportCounts[k] > 0);
    const maxC = Math.max(...recordedSports.map(([k]) => sportCounts[k]), 1);

    // 주간 데이터 (최근 4주)
    const last4Weeks = Array.from({ length: 4 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (3 - i) * 7);
        const wS = new Date(d);
        wS.setDate(d.getDate() - d.getDay());
        const wE = new Date(wS);
        wE.setDate(wS.getDate() + 6);
        wS.setHours(0, 0, 0, 0);
        wE.setHours(23, 59, 59, 999);
        const cnt = records.filter(r => {
            const rd = parseDateStr(r.date);
            return rd >= wS && rd <= wE;
        }).length;
        return { label: weekOfMonthLabel(wS), cnt };
    });
    const maxW = Math.max(...last4Weeks.map(w => w.cnt), 1);
    const currentWeek = last4Weeks[last4Weeks.length - 1]?.cnt || 0;
    const prevWeek = last4Weeks[last4Weeks.length - 2]?.cnt || 0;
    const weekDiff = currentWeek - prevWeek;
    const topSport = Object.entries(sportCounts).filter(([, cnt]) => cnt > 0).sort((a, b) => b[1] - a[1])[0];
    const activeGoalCount = showGoals ? (goals || []).filter(g => goalProgress(g) < g.target).length : 0;

    // PR
    const prMap = {};
    records.filter(r => r.sport === 'weight').forEach(r => {
        const exList = r.exercises?.length
            ? r.exercises
            : (r.exerciseName ? [{ name: r.exerciseName, muscleGroup: r.muscleGroup || '', sets: r.sets || [] }] : []);
        exList.forEach(ex => {
            if (!ex.name || !ex.sets?.length) return;
            ex.sets.forEach(set => {
                const oneRm = estimateOneRm(set.weight, set.reps);
                if (!oneRm) return;
                if (!prMap[ex.name] || oneRm > prMap[ex.name].oneRm) {
                    prMap[ex.name] = {
                        oneRm,
                        weight: +set.weight || 0,
                        reps: +set.reps || 0,
                        date: r.date,
                        muscleGroup: ex.muscleGroup || '',
                    };
                }
            });
        });
    });

    const streak = calcStreak(records);

    return (
        <>
            <div className="insight-card">
                <div>
                    <div className="insight-title">이번 주 {currentWeek}회</div>
                    <div className="insight-desc">
                        {weekDiff === 0 ? '지난주와 같은 페이스예요' : `지난주보다 ${weekDiff > 0 ? '+' : ''}${weekDiff}회`}
                        {topSport?.[1] > 0 ? ` · 가장 많이 한 종목은 ${SPORTS[topSport[0]]?.name || topSport[0]}` : ''}
                    </div>
                </div>
                {showGoals && <div className="insight-pill">{activeGoalCount ? `목표 ${activeGoalCount}개 진행중` : '목표 없음'}</div>}
            </div>

            {showGoals && (
                <>
                    {/* 목표 관리 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 10 }}>
                        <p className="section-label" style={{ margin: 0 }}>목표</p>
                        <button className="goal-add-btn" onClick={() => setShowAddGoal(v => !v)}>
                            {showAddGoal ? '취소' : '+ 목표 추가'}
                        </button>
                    </div>

                    {showAddGoal && (
                        <div className="goal-add-panel">
                            <p style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono,monospace', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>새 목표 설정</p>

                            <div style={{ marginBottom: 10 }}>
                                <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>종목</p>
                                <select className="form-input" value={newGoal.sport} onChange={e => setNewGoal(g => ({ ...g, sport: e.target.value }))}>
                                    <option value="all">전체 종목</option>
                                    {visibleSports.map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
                                </select>
                            </div>

                            <div style={{ marginBottom: 12 }}>
                                <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>기간</p>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button
                                        className={`toggle-btn${newGoal.type === 'weekly' ? ' active' : ''}`}
                                        style={{ flex: 1, textAlign: 'center' }}
                                        onClick={() => setNewGoal(g => ({ ...g, type: 'weekly' }))}
                                    >주간</button>
                                    <button
                                        className={`toggle-btn${newGoal.type === 'monthly' ? ' active' : ''}`}
                                        style={{ flex: 1, textAlign: 'center' }}
                                        onClick={() => setNewGoal(g => ({ ...g, type: 'monthly' }))}
                                    >월간</button>
                                </div>
                            </div>

                            <div style={{ marginBottom: 14 }}>
                                <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>목표 횟수</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <button className="goal-count-btn" onClick={() => setNewGoal(g => ({ ...g, target: Math.max(1, +g.target - 1) }))}>−</button>
                                    <span style={{ fontSize: 28, fontFamily: 'DM Mono,monospace', color: 'var(--text)', minWidth: 48, textAlign: 'center', fontWeight: 300 }}>{newGoal.target}</span>
                                    <button className="goal-count-btn" onClick={() => setNewGoal(g => ({ ...g, target: Math.min(99, +g.target + 1) }))}>+</button>
                                    <span style={{ fontSize: 13, color: 'var(--text3)' }}>회</span>
                                </div>
                            </div>

                            <button className="save-btn" style={{ marginTop: 0 }} onClick={addGoal}>저장하기</button>
                        </div>
                    )}

                    {(!goals || goals.length === 0) && !showAddGoal && (
                        <div style={{ textAlign: 'center', padding: '20px 0 16px', color: 'var(--text3)', fontSize: 13 }}>
                            아직 설정된 목표가 없어요
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
                        {(goals || []).map((g, i) => {
                            const current = goalProgress(g);
                            const pct = Math.min(1, current / g.target);
                            const done = pct >= 1;
                            const color = g.sport === 'all' ? 'var(--accent)' : (SPORTS[g.sport]?.color || 'var(--accent)');
                            const sportLabel = g.sport === 'all' ? '전체' : (SPORTS[g.sport]?.name || g.sport);
                            const remaining = Math.max(0, g.target - current);
                            return (
                                <div key={g.id || i} className="goal-card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                                                <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{sportLabel}</span>
                                                <span style={{ fontSize: 11, color: 'var(--text3)', background: 'var(--bg3)', padding: '2px 7px', borderRadius: 20, fontFamily: 'DM Mono,monospace' }}>
                                                    {g.type === 'weekly' ? '주간' : '월간'}
                                                </span>
                                            </div>
                                            {done
                                                ? <span style={{ fontSize: 12, color }}>달성 완료!</span>
                                                : <span style={{ fontSize: 12, color: 'var(--text3)' }}>{remaining}회 남음</span>
                                            }
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 22, fontFamily: 'DM Mono,monospace', fontWeight: 300, color: done ? color : 'var(--text)' }}>
                                                {current}<span style={{ fontSize: 13, color: 'var(--text3)' }}>/{g.target}</span>
                                            </span>
                                            <button className="goal-remove-btn" onClick={() => removeGoal(g.id || i)}>×</button>
                                        </div>
                                    </div>
                                    <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${pct * 100}%`, background: color, borderRadius: 3, transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)' }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <section className="stats-section">
                <div className="stats-section-head">
                    <div>
                        <div className="stats-section-title">운동 흐름</div>
                        <div className="stats-section-desc">최근 주간 운동량과 종목 분포</div>
                    </div>
                </div>
                <div className="chart-wrap">
                    <div className="chart-title">
                        <span>주간 운동 횟수</span>
                        <span style={{ fontFamily: 'DM Mono,monospace', color: 'var(--accent)' }}>
                            총 {currentWeek}회
                        </span>
                    </div>
                    <div className="bar-chart" style={{ alignItems: 'flex-end', gap: 4 }}>
                        {last4Weeks.map((w, i) => {
                            const isLatest = i === last4Weeks.length - 1;
                            const h = Math.round((w.cnt / maxW) * 72) + 4;
                            return (
                                <div key={i} className="chart-bar-wrap">
                                    {w.cnt > 0 && (
                                        <span style={{ fontSize: 10, fontFamily: 'DM Mono,monospace', color: isLatest ? 'var(--accent)' : 'var(--text2)', marginBottom: 3 }}>
                                            {w.cnt}
                                        </span>
                                    )}
                                    <div
                                        className="chart-bar"
                                        style={{
                                            height: h,
                                            background: w.cnt ? (isLatest ? 'var(--accent)' : 'var(--bg4)') : 'var(--bg3)',
                                            border: isLatest && w.cnt ? '0.5px solid var(--accent)' : 'none',
                                        }}
                                    />
                                    <div className="chart-label" style={{ color: isLatest ? 'var(--text2)' : 'var(--text3)' }}>{w.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {recordedSports.length > 0 && (
                    <div className="chart-wrap">
                        <div className="chart-title">
                            <span>종목별 누적 횟수</span>
                            <span style={{ fontFamily: 'DM Mono,monospace', color: 'var(--text2)' }}>
                                {Object.values(sportCounts).reduce((a, b) => a + b, 0)}회 총계
                            </span>
                        </div>
                        <div className="bar-chart" style={{ alignItems: 'flex-end', gap: 4 }}>
                            {recordedSports.map(([k, v]) => {
                                const cnt = sportCounts[k];
                                const h = Math.round((cnt / maxC) * 68) + 4;
                                return (
                                    <div key={k} className="chart-bar-wrap">
                                        <span style={{ fontSize: 10, fontFamily: 'DM Mono,monospace', color: v.color, marginBottom: 3 }}>
                                            {cnt}
                                        </span>
                                        <div className="chart-bar" style={{ height: h, background: v.color }} />
                                        <div className="chart-label">{v.name.split('·')[0].trim()}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>

            {promotions.length > 0 && <section className="stats-section">
                <BjjPromotionSummary promotions={promotions} />
            </section>}


            {(weights.length > 0 || Object.keys(prMap).length > 0) && (
                <section className="stats-section">
                    <div className="stats-section-head">
                        <div>
                            <div className="stats-section-title">몸 변화와 성장</div>
                            <div className="stats-section-desc">체중 변화와 웨이트 PR</div>
                        </div>
                    </div>
                    {weights.length > 0 && <WeightChart weights={weights} />}
                    {Object.keys(prMap).length > 0 && (
                        <>
                            <WeightPrTrend records={records} />
                            <div className="pr-list">
                                {Object.entries(prMap)
                                    .sort(([, a], [, b]) => b.oneRm - a.oneRm)
                                    .slice(0, 8)
                                    .map(([name, pr]) => (
                                        <div key={name} className="pr-item">
                                            <span className="pr-name">
                                                {name}
                                                <em>{pr.muscleGroup || '웨이트'} · {pr.date} · {pr.weight}kg×{pr.reps}</em>
                                            </span>
                                            <span className="pr-val">{pr.oneRm}kg</span>
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}
                </section>
            )}

            <section className="stats-section">
                <div className="stats-section-head">
                    <div>
                        <div className="stats-section-title">누적 요약</div>
                        <div className="stats-section-desc">지금까지 쌓은 전체 기록</div>
                    </div>
                </div>
                <div className="stats-row">
                    <div className="stat-card"><div className="stat-val">{records.length}개</div><div className="stat-label">총 기록 수</div></div>
                    <div className="stat-card"><div className="stat-val">{streak}일</div><div className="stat-label">연속 운동 일수</div></div>
                    <div className="stat-card"><div className="stat-val">{Math.round(records.reduce((a, r) => a + (+r.duration || 0), 0) / 60) || 0}h</div><div className="stat-label">총 시간</div></div>
                </div>
            </section>
        </>
    );
}
